import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { Metrics } from 'components/insights/constants';
import { formatPercentage } from 'components/insights/formatting';
import { colors } from 'styles/theme/colors';

import MetricDetailLayout from '../../../shared/slideout/MetricDetailLayout';
import { readGrandTotal } from '../../../shared/slideout/rollupHelpers';
import { StatTile } from '../../../shared/slideout/statTiles';
import { METRIC_CONFIGS, NetworkPerformanceMetrics } from '../constants';
import { type MetricHeaderProps } from '../NetworkPerformanceSlideout';

import { buildColumnChartOptions, withReferenceLine } from './chartHelpers';

export default function OesHeader({
  loading,
  rollups,
  highestEntity,
  lowestEntity,
  entityRows,
  entityLabel,
}: MetricHeaderProps) {
  const overallOES = readGrandTotal(rollups, Metrics.OLIO_ENGAGEMENT_SCORE);

  const { options, scrollConfig: baseScrollConfig } = useMemo(
    () =>
      buildColumnChartOptions({
        entityRows,
        seriesName: METRIC_CONFIGS[NetworkPerformanceMetrics.OES].primaryMetricLabel,
        color: colors.blue100,
        yAxisFormat: '{value}%',
        dataLabelsFormatter: function () {
          return formatPercentage(this.y ?? null, 0);
        },
      }),
    [entityRows]
  );

  const chartOptions = useMemo(
    () =>
      withReferenceLine(options, {
        value: 100,
        label: 'OES Goal (100%)',
        yAxisMax: Math.max(100, ...entityRows.map((r) => r.value)),
      }),
    [options, entityRows]
  );

  return (
    <MetricDetailLayout
      tiles={
        <>
          <StatTile
            label={METRIC_CONFIGS[NetworkPerformanceMetrics.OES].primaryMetricLabel}
            loading={loading}
            primary={formatPercentage(overallOES, 0)}
            addendum='Goal: 100%'
          />
          <StatTile
            label='Most Engaged'
            loading={loading}
            primary={highestEntity?.name ?? '\u2014'}
            size='medium'
            addendum={highestEntity ? formatPercentage(highestEntity.value, 0) : undefined}
            tone='green'
          />
          <StatTile
            label='Least Engaged'
            loading={loading}
            primary={lowestEntity?.name ?? '\u2014'}
            size='medium'
            addendum={lowestEntity ? formatPercentage(lowestEntity.value, 0) : undefined}
            tone='red'
          />
        </>
      }
      chartTitle={`${METRIC_CONFIGS[NetworkPerformanceMetrics.OES].label} by ${entityLabel}`}
      chartLoading={loading}
      isEmpty={entityRows.length === 0}
      scrollConfig={{ minWidth: baseScrollConfig.minWidth }}
      emptyMessage='No engagement data to show for this time period.'>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </MetricDetailLayout>
  );
}
